import { type ComponentType, useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Home24Regular, LeafTwo24Regular, MoneyHand24Regular,
  CalendarLtr24Regular,
  Drop24Regular, BoxMultiple24Regular, Star24Regular,
  VehicleTruck24Regular, Beaker24Regular, Airplane24Regular,
  Dismiss24Regular, DocumentPdf24Regular, Map24Regular, Chat24Regular,
  People24Regular, BuildingMultiple24Regular,
  Delete24Regular, PlantGrass24Regular,
  ShieldCheckmark24Regular,
  GanttChart24Regular, Router24Regular, BookOpen24Regular,
  Folder24Regular, Warning24Regular, CheckmarkCircle24Regular, Image24Regular,
  ChevronDown20Regular, ChevronRight20Regular,
} from '@fluentui/react-icons';
import { makeStyles, tokens, mergeClasses, Badge } from '@fluentui/react-components';
import { useT } from '../../i18n';
import { fetchIncompleteDrafts } from '../../api/incomplete-drafts.api';
import { fetchPendingRegistrations } from '../../api/pendingRegistrations';
import { useAuthStore } from '../../store/auth.store';
import { getTenantFeatures } from '../../api/tenant.api';
import { fetchScopedConfig } from '../../api/config-scope.api';
import { TENANT_FEATURES } from '../../constants/features';
import type { Role } from '../../types/api.types';
import { usePermissions } from '../../hooks/usePermission';
import type { Permission } from '../../api/permissions.api';
import {
  findActiveGroupId, loadOpenGroups, saveOpenGroups, NAV_EXPAND_ALL_EVENT, NAV_RESTORE_EVENT,
} from './nav-model';

/** Returns true if the user has at least one of the required roles. */
function hasAnyRole(userRoles: Role[], allowed: Role[]): boolean {
  return allowed.some(r => userRoles.includes(r));
}

/** Map route path segments to feature keys.
 * Routes starting with 'super-admin/' are NEVER filtered by features — they're always visible to SUPER_ADMIN.
 * Use null to mark routes that should bypass feature filtering.
 */
const PATH_TO_FEATURE: Record<string, string | null> = {
  'dashboard': 'dashboard',
  'investor/overview': null, // Nunca filtrado por feature flags — visibilidad gobernada por rol/permiso
  'rendimiento': null,
  'logistica': null,
  'fincas': null,
  'fincas/modulo': null,
  'inversion': null,
  'costos': null, // Fase D — sin feature flag propio, mismo criterio que inversion/produccion
  'calculadoras': null, // gobernado solo por calculators:read
  'gastos': null, // B3 — gobernado solo por expenses:read
  'gastos-aprobaciones': null, // B2 — gobernado solo por expenses:approve
  'produccion': null,
  'campo': 'field',
  'tareas': 'tasks',
  'chat': 'chat',
  'finanzas': 'finance',
  'usuarios': 'users',
  'riego': 'irrigation',
  'cosecha': 'harvest',
  'inventario': 'inventory',
  'maquinaria': 'machinery',
  'drones': 'machinery',
  'aplicacion': 'spray',
  'merma': 'merma',
  'cultivo': 'crop',
  'nomina': 'payroll',
  'porcino': 'porcino',
  'exportacion': 'export',
  'transporte': 'transport',
  'trazabilidad': 'traceability',
  'mapa': 'map',
  'reportes': 'reports',
  'protocolos': 'protocol',
  'permisos': 'users',
  'ranchos': 'field',
  'vocabulario': 'field',
  'ventas': null, // F3/M08 — sin feature flag propio; gobernado solo por permiso/rol
  'super-admin': null, // Never filter — always visible to SUPER_ADMIN
  'super-admin/config': null, // Never filter — always visible to SUPER_ADMIN
  'dispositivos': null, // F5 — sin feature flag propio todavía (TENANT_FEATURES no tiene 'devices'); gobernado solo por permiso/rol, como super-admin
  'sistema': null,
  'primeros-pasos': null,
  'documentos': null, // Fase C — sin feature flag propio, gobernado solo por permiso/rol
  'evidencias': null, // visor de fotos de evidencia — solo admins (audit:read)
  'personal': 'attendance',   // asistencia por rostro: apagada salvo que la empresa la active
  'asistencia': 'attendance',
  'caseta': 'attendance',
  'reglas-cumplimiento': null, // sin feature flag propio; gobernado por compliance:read
  'riesgos': null,    // Fase C — sin feature flag propio, gobernado solo por permiso/rol
};


/**
 * F0.3 — Cada entrada del menú declara el permiso que exige el API detrás.
 *
 * `ROLE_ALLOWED_PATHS` era la tercera copia de la matriz de permisos (backend
 * `rbac.model.ts`, frontend `usePermission`, y esta por rutas): el menú ofrecía
 * secciones que el API rechazaba con 403 y escondía otras que sí estaban
 * permitidas. Ahora los permisos efectivos los sirve el backend y aquí solo se
 * mapea ruta → permiso.
 */
const PATH_TO_PERMISSION: Record<string, Permission> = {
  'dashboard':          'dashboard:read',
  'investor/overview':  'dashboard:read',
  'rendimiento':     'harvest:read',
  'logistica':       'export:read',
  'fincas':          'projects:read',
  'fincas/modulo':   'projects:read',
  'inversion':       'finance:read',
  'costos':          'finance:read',
  'calculadoras':    'calculators:read',
  'gastos':          'expenses:read',
  'gastos-aprobaciones': 'expenses:approve',
  'produccion':      'production:read',
  'campo':              'lotes:read',
  'tareas':             'tasks:read',
  // Cualquier usuario autenticado puede tener drafts propios incompletos —
  // reusa 'dashboard:read' (casi cualquier rol lo tiene) en vez de un
  // permiso nuevo, mismo criterio que la propia página 'dashboard'.
  'registros-incompletos': 'dashboard:read',
  'primeros-pasos':     'dashboard:read',
  'riego':              'irrigation:read',
  'aplicacion':         'crop:read',
  'cosecha':            'harvest:read',
  'cultivo':            'crop:read',
  'protocolos':         'protocol:read',
  'inventario':         'inventory:read',
  'maquinaria':         'machinery:read',
  'drones':             'machinery:read',
  'porcino':            'porcino:read',
  'nomina':             'payroll:read',
  'merma':              'merma:read',
  'trazabilidad':       'traceability:read',
  'trazabilidad/fsma':  'traceability:read',
  'transporte':         'transport:read',
  'exportacion':        'export:read',
  'finanzas':           'finance:read',
  'reportes':           'reports:read',
  'mapa':               'map:read',
  'chat':               'chat:use',
  'usuarios':           'users:read',
  'ranchos':            'admin:ranchos',
  'permisos':           'admin:rbac',
  'vocabulario':        'vocabulary:read',
  'super-admin':        'admin:super',
  // F0-unify: el propio componente gatea sus tabs administrativos por rol;
  // dashboard:read solo garantiza que el tab de Idioma (antes en /ajustes) sea
  // visible a todos.
  'super-admin/config': 'dashboard:read',
  'dispositivos':       'devices:read',
  'ventas':             'sales:read',
  'documentos':         'reports:read',
  'evidencias':         'audit:read',
  'personal':           'attendance:read',
  'asistencia':         'attendance:read',
  'caseta':             'attendance:gate',
  'riesgos':            'anomalies:read',
  'reglas-cumplimiento': 'compliance:read',
};

const SIDEBAR_WIDTH = '220px';

const useStyles = makeStyles({
  sidebar: {
    width: SIDEBAR_WIDTH,
    minWidth: SIDEBAR_WIDTH,
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRight: `1px solid ${tokens.colorNeutralStroke1}`,
    display: 'flex',
    flexDirection: 'column',
    padding: '0',
    overflowY: 'auto',
    transition: 'transform 0.25s ease',
    /* On mobile: overlay + slide-in behavior */
    '@media (max-width: 768px)': {
      position: 'fixed',
      top: '0',
      left: '0',
      bottom: '0',
      zIndex: '100',
      transform: `translateX(-${SIDEBAR_WIDTH})`,
      boxShadow: '4px 0 16px rgba(0,0,0,0.15)',
    },
  },
  sidebarOpen: {
    '@media (max-width: 768px)': {
      transform: 'translateX(0)',
    },
  },
  closeBtn: {
    display: 'none',
    '@media (max-width: 768px)': {
      display: 'flex',
      justifyContent: 'flex-end',
      padding: '8px 12px 0',
    },
  },
  closeBtnInner: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    color: tokens.colorNeutralForeground2,
    display: 'flex',
    alignItems: 'center',
    borderRadius: tokens.borderRadiusMedium,
    ':hover': { backgroundColor: tokens.colorNeutralBackground3Hover },
  },
  logoWrap: {
    padding: '14px 16px 14px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    marginBottom: '4px',
  },
  logoText: {
    fontSize: '18px',
    fontWeight: '700',
    color: tokens.colorBrandForeground1,
    letterSpacing: '-0.3px',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  logoIcon: {
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
  },
  logoImg: {
    height: '52px',
    width: 'auto',
    display: 'block',
  },
  logoSub: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    marginTop: '2px',
  },
  sectionLabel: {
    padding: '8px 16px 3px',
    fontSize: '9.5px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
  },
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    width: '100%',
    padding: '7px 16px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
    color: tokens.colorNeutralForeground1,
    fontSize: '12.5px',
    fontWeight: '600',
    ':hover': { backgroundColor: tokens.colorNeutralBackground3Hover },
  },
  groupLabel: { flex: 1 },
  groupBody: { display: 'flex', flexDirection: 'column' },
  // `hidden` por sí solo pierde contra `display: flex` de groupBody: se oculta con una clase explícita.
  groupBodyClosed: { display: 'none' },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 16px',
    textDecoration: 'none',
    color: tokens.colorNeutralForeground2,
    fontSize: '12.5px',
    borderLeft: '3px solid transparent',
    transition: 'background 0.1s, border-color 0.1s',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
      color: tokens.colorNeutralForeground1,
    },
  },
  navActive: {
    backgroundColor: tokens.colorBrandBackground2,
    borderLeftColor: tokens.colorBrandBackground,
    color: tokens.colorBrandForeground1,
    fontWeight: '600',
  },
  divider: {
    height: '1px',
    backgroundColor: tokens.colorNeutralStroke2,
    margin: '4px 16px',
  },
});

interface NavEntry {
  to: string;
  labelKey: keyof ReturnType<typeof useT>['nav'];
  Icon: ComponentType<{ className?: string }>;
  end?: boolean;
  /** If set, only render this item when the user has at least one of these roles. */
  roles?: Role[];
  /** Optional onboarding-tour hook — matched by `TOUR_STEPS` selectors (e.g. `[data-tour="nav-dashboard"]`). */
  tourId?: string;
  /** Solo visible si la empresa tiene `fsma.enabled` activo (falla cerrada: sin config, oculto). */
  requiresFsma?: boolean;
}


const ADMIN_PLUS: Role[] = ['SUPER_ADMIN', 'ADMIN'];
const MGMT_PLUS: Role[] = ['SUPER_ADMIN', 'ADMIN', 'AGRONOMIST', 'SUPERVISOR'];
const WAREHOUSE_PLUS: Role[] = ['SUPER_ADMIN', 'ADMIN', 'WAREHOUSE_MANAGER', 'SUPERVISOR'];
/** `dashboard` e `investor/overview` comparten el mismo permiso (`dashboard:read`, casi
 * cualquier rol lo tiene) pero son landing pages EXCLUYENTES entre sí, no ítems de
 * dominio — sin este gate, cualquier usuario con ambos roles vería las dos "Inicio"
 * a la vez. Reagrupar el Sidebar en Fase A retiró el gate de rol de los ítems de
 * dominio (correcto, ver commit 08174ec) pero estos dos no son de dominio. */
const NON_INVESTOR: Role[] = ['SUPER_ADMIN', 'ADMIN', 'AGRONOMIST', 'SUPERVISOR', 'WAREHOUSE_MANAGER', 'FIELD_WORKER', 'AUDITOR'];
const INVESTOR_PLUS: Role[] = ['INVESTOR', 'SUPER_ADMIN'];
/** Todos los roles menos RH: pantallas que RH no necesita ver en su menú (si además tiene otro rol, las ve). */
const NOT_HR_ONLY: Role[] = ['SUPER_ADMIN', 'ADMIN', 'AGRONOMIST', 'SUPERVISOR', 'WAREHOUSE_MANAGER', 'FIELD_WORKER', 'AUDITOR', 'INVESTOR', 'GUARD'];

/** Ítems sin grupo, siempre visibles. `dashboard` e `investor/overview` son excluyentes por rol. */
const TOP_NAV: NavEntry[] = [
  { to: 'dashboard',    labelKey: 'dashboard',        Icon: Home24Regular,          end: true, roles: NON_INVESTOR, tourId: 'nav-dashboard' },
  { to: 'investor/overview', labelKey: 'investorOverview', Icon: Home24Regular,     end: true, roles: INVESTOR_PLUS },
  { to: 'chat',         labelKey: 'chat',              Icon: Chat24Regular },
];

/** "Mi día": lo que el usuario atiende a diario. */
const DAY_NAV: NavEntry[] = [
  { to: 'tareas',       labelKey: 'tasks',             Icon: CalendarLtr24Regular,  tourId: 'nav-tareas' },
  { to: 'registros-incompletos', labelKey: 'incompleteDrafts', Icon: Warning24Regular, roles: NOT_HR_ONLY },
];

type NavGroupId = 'campo' | 'cosechaVenta' | 'recursos' | 'finanzas' | 'cumplimiento' | 'admin';

interface NavGroupDef {
  id: NavGroupId;
  items: NavEntry[];
}

/**
 * Menú agrupado por flujo de trabajo (spec 2026-10-07-menu-por-flujos-design.md). Cada ruta
 * aparece una sola vez. La visibilidad de cada ítem (roles, permiso, feature flag) no cambia.
 */
const NAV_GROUPS: NavGroupDef[] = [
  {
    id: 'campo',
    items: [
      { to: 'campo',        labelKey: 'fieldLots',        Icon: LeafTwo24Regular,       end: true, roles: NOT_HR_ONLY, tourId: 'nav-campo-lotes' },
      { to: 'mapa',         labelKey: 'map',               Icon: Map24Regular },
      { to: 'fincas',       labelKey: 'investorModules',  Icon: Map24Regular },
      { to: 'cultivo',      labelKey: 'crop',              Icon: PlantGrass24Regular,   roles: MGMT_PLUS },
      { to: 'riego',        labelKey: 'irrigation',        Icon: Drop24Regular },
      { to: 'aplicacion',   labelKey: 'spray',             Icon: Beaker24Regular },
      { to: 'protocolos',   labelKey: 'protocol',          Icon: DocumentPdf24Regular,  roles: MGMT_PLUS },
    ],
  },
  {
    id: 'cosechaVenta',
    items: [
      { to: 'cosecha',      labelKey: 'harvest',           Icon: Star24Regular },
      { to: 'merma',        labelKey: 'merma',             Icon: Delete24Regular },
      { to: 'trazabilidad', labelKey: 'traceability',      Icon: BoxMultiple24Regular,  roles: MGMT_PLUS },
      { to: 'trazabilidad/fsma', labelKey: 'fsmaExport',     Icon: DocumentPdf24Regular,  roles: MGMT_PLUS, requiresFsma: true },
      { to: 'produccion',   labelKey: 'investorProduction', Icon: GanttChart24Regular },
      { to: 'rendimiento',  labelKey: 'investorYield',     Icon: Star24Regular },
      { to: 'logistica',    labelKey: 'investorLogistics', Icon: VehicleTruck24Regular },
      { to: 'transporte',   labelKey: 'transport',         Icon: VehicleTruck24Regular, roles: ADMIN_PLUS },
      { to: 'exportacion',  labelKey: 'export',            Icon: Star24Regular,         roles: ADMIN_PLUS },
      { to: 'ventas',       labelKey: 'sales',             Icon: MoneyHand24Regular,    roles: ADMIN_PLUS },
    ],
  },
  {
    id: 'recursos',
    items: [
      { to: 'inventario',   labelKey: 'inventory',    Icon: BoxMultiple24Regular,   roles: WAREHOUSE_PLUS, tourId: 'nav-insumos' },
      { to: 'maquinaria',   labelKey: 'machinery',    Icon: VehicleTruck24Regular,  roles: MGMT_PLUS },
      { to: 'drones',       labelKey: 'drones',       Icon: Airplane24Regular,      roles: MGMT_PLUS },
      { to: 'nomina',       labelKey: 'payroll',      Icon: MoneyHand24Regular,     roles: ADMIN_PLUS },
      { to: 'porcino',      labelKey: 'porcino',      Icon: Star24Regular },
    ],
  },
  {
    id: 'finanzas',
    items: [
      { to: 'finanzas',   labelKey: 'finance',          Icon: MoneyHand24Regular, tourId: 'nav-finanzas' },
      { to: 'costos',     labelKey: 'costos',           Icon: MoneyHand24Regular },
      { to: 'calculadoras', labelKey: 'calculators', Icon: MoneyHand24Regular },
      { to: 'gastos', labelKey: 'expensesDashboard', Icon: MoneyHand24Regular },
      { to: 'gastos-aprobaciones', labelKey: 'expenseApprovals', Icon: CheckmarkCircle24Regular },
      { to: 'inversion',  labelKey: 'investorFunding',  Icon: MoneyHand24Regular },
    ],
  },
  {
    id: 'cumplimiento',
    items: [
      { to: 'riesgos',    labelKey: 'riesgos',    Icon: Warning24Regular },
      { to: 'reglas-cumplimiento', labelKey: 'complianceRules', Icon: ShieldCheckmark24Regular },
      { to: 'reportes',   labelKey: 'reports',    Icon: DocumentPdf24Regular },
      { to: 'documentos', labelKey: 'documents',  Icon: Folder24Regular },
      { to: 'evidencias', labelKey: 'evidence', Icon: Image24Regular },
      { to: 'personal', labelKey: 'personnel', Icon: People24Regular },
      { to: 'asistencia', labelKey: 'attendance', Icon: CheckmarkCircle24Regular },
      { to: 'caseta', labelKey: 'gate', Icon: ShieldCheckmark24Regular },
    ],
  },
  {
    id: 'admin',
    items: [
      { to: 'usuarios',     labelKey: 'users',      Icon: People24Regular, roles: NOT_HR_ONLY },
      { to: 'ranchos',      labelKey: 'ranchos',    Icon: BuildingMultiple24Regular, roles: ADMIN_PLUS },
      { to: 'permisos',     labelKey: 'permisos',   Icon: ShieldCheckmark24Regular,  roles: ADMIN_PLUS },
      { to: 'vocabulario',  labelKey: 'vocabulary', Icon: DocumentPdf24Regular,      roles: MGMT_PLUS },
      { to: 'dispositivos', labelKey: 'devices',    Icon: Router24Regular,           roles: ADMIN_PLUS },
      { to: 'super-admin',  labelKey: 'companies',  Icon: BuildingMultiple24Regular, roles: ['SUPER_ADMIN'], end: true },
      { to: 'primeros-pasos', labelKey: 'gettingStarted', Icon: Star24Regular, roles: ADMIN_PLUS },
      { to: 'sistema',      labelKey: 'system',    Icon: BookOpen24Regular },
    ],
  },
];

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export function Sidebar({ isOpen = false, onClose }: SidebarProps) {
  const s = useStyles();
  const t = useT();
  const { tenantId } = useParams<{ tenantId: string }>();
  const { user } = useAuthStore();
  const [enabledFeatures, setEnabledFeatures] = useState<Record<string, boolean>>({});

  const userRoles: Role[] = (user?.roles ?? []) as Role[];
  // F0.3: permisos efectivos servidos por el backend desde rbac.model.ts
  const { permissions } = usePermissions();

  // Load farm modules configuration from tenant config (UUID de sesión, solo con config:read)
  const sessionTenantId = user?.tenantId;
  const canReadConfig = permissions.has('config:read');
  const { data: tenantConfigData } = useQuery({
    queryKey: ['tenant-config', sessionTenantId],
    queryFn: () => sessionTenantId ? fetchScopedConfig('TENANT', sessionTenantId) : Promise.resolve(null),
    enabled: !!sessionTenantId && canReadConfig,
  });
  // Fail open: if config hasn't loaded, show the link to avoid flickering
  const useFarmModules = tenantConfigData?.effective['modules.useFarmModules']?.value !== false;
  // Trazabilidad FSMA 204: falla cerrada. Sin config cargada, el enlace no aparece.
  const fsmaEnabled = tenantConfigData?.effective['fsma.enabled']?.value === true;

  // Fase 3 (registro incompleto) — conteo de drafts propios pendientes, mismo
  // query key que IncompleteDraftsPage (se cachea, no duplica la llamada si
  // el usuario ya visitó la página).
  const { data: incompleteDrafts } = useQuery({
    queryKey: ['incomplete-drafts'],
    queryFn: fetchIncompleteDrafts,
  });
  const incompleteDraftsCount = incompleteDrafts?.length ?? 0;

  // Solicitudes de registro pendientes (registro por enlace): solo para quien puede
  // verlas (`users:read`). Mismo query key que el tab "Solicitudes" de Usuarios.
  const canReadUsers = permissions.has('users:read');
  const { data: pendingRegistrations } = useQuery({
    queryKey: ['pendingRegistrations', tenantId],
    queryFn: () => fetchPendingRegistrations(tenantId ?? ''),
    enabled: canReadUsers,
  });
  const pendingRegistrationsCount = pendingRegistrations?.length ?? 0;

  // Load tenant config to get feature overrides
  useEffect(() => {
    if (!tenantId) return;
    getTenantFeatures()
      .then(config => {
        // Start with all features enabled (defaults), then apply overrides
        const defaults: Record<string, boolean> = {};
        Object.values(TENANT_FEATURES).forEach(feature => {
          defaults[feature.key] = feature.default;
        });
        setEnabledFeatures({
          ...defaults,
          ...(config.featureFlags ?? {}),
        });
      })
      .catch(() => {
        // Fallback to all defaults if config fails to load
        const defaults: Record<string, boolean> = {};
        Object.values(TENANT_FEATURES).forEach(feature => {
          defaults[feature.key] = feature.default;
        });
        setEnabledFeatures(defaults);
      });
  }, [tenantId]);

  /** Ruta visible solo si el usuario tiene el permiso que exige el API detrás. */
  const hasNavPermission = (to: string): boolean => {
    const required = PATH_TO_PERMISSION[to];
    if (!required) return false; // ruta sin permiso declarado → no se muestra
    return permissions.has(required);
  };

  // ── Grupos plegables (spec menú por flujos) ────────────────────────────────
  const location = useLocation();
  const navigate = useNavigate();
  const userKey = user?.phoneNumber ?? 'anon';
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => loadOpenGroups(userKey));
  const [expandAll, setExpandAll] = useState(false);

  // Auto-abrir el grupo que contiene la ruta activa.
  useEffect(() => {
    const active = findActiveGroupId(location.pathname, tenantId ?? 'demo', NAV_GROUPS);
    if (!active) return;
    setOpenGroups((prev) => {
      if (prev[active]) return prev;
      const next = { ...prev, [active]: true };
      saveOpenGroups(userKey, next);
      return next;
    });
  }, [location.pathname, tenantId, userKey]);

  // El tour guiado necesita ver todos los ítems: abre todo mientras dura y luego restaura.
  useEffect(() => {
    const open = () => setExpandAll(true);
    const restore = () => setExpandAll(false);
    window.addEventListener(NAV_EXPAND_ALL_EVENT, open);
    window.addEventListener(NAV_RESTORE_EVENT, restore);
    return () => {
      window.removeEventListener(NAV_EXPAND_ALL_EVENT, open);
      window.removeEventListener(NAV_RESTORE_EVENT, restore);
    };
  }, []);

  /**
   * Abre/cierra un grupo. Al ABRIRLO desde fuera del grupo también entra a su primer módulo
   * visible, para no obligar a un segundo clic; al cerrarlo no navega.
   */
  const toggleGroup = (group: NavGroupDef) => {
    const wasOpen = !!openGroups[group.id];
    setOpenGroups((prev) => {
      const next = { ...prev, [group.id]: !prev[group.id] };
      saveOpenGroups(userKey, next);
      return next;
    });
    if (wasOpen) return;
    const tenant = tenantId ?? 'demo';
    if (findActiveGroupId(location.pathname, tenant, NAV_GROUPS) === group.id) return;
    const first = group.items.find(canRender);
    if (first) navigate(`/${tenant}/${first.to}`);
  };

  const handleNavClick = () => {
    // Close sidebar on mobile after navigation
    onClose?.();
  };

  /** Condiciones con las que `renderLink` pinta (o no) un ítem (permiso, rol, feature de empresa y módulos); los grupos se muestran si algún ítem se pinta.
   * Routes with null featureKey (e.g., super-admin/*) bypass feature filtering entirely.
   */
  const canRender = ({ to, roles, requiresFsma }: NavEntry): boolean => {
    if (!hasNavPermission(to)) return false;
    if (roles && !hasAnyRole(userRoles, roles)) return false;
    // Feature deshabilitada para la empresa (Editar empresa): no se muestra el enlace.
    const featureKey = PATH_TO_FEATURE[to];
    if (featureKey !== null && featureKey !== undefined && enabledFeatures[featureKey] === false) return false;
    if (to === 'caseta' && enabledFeatures.attendanceGate === false) return false;
    if (to === 'fincas' && !useFarmModules) return false;
    if (requiresFsma && !fsmaEnabled) return false;
    return true;
  };

  const renderLink = (entry: NavEntry) => {
    if (!canRender(entry)) return null;
    const { to, labelKey, Icon, end, tourId } = entry;
    const fullPath = `/${tenantId ?? 'demo'}/${to}`;
    return (
      <NavLink
        key={to}
        to={fullPath}
        end={end}
        onClick={handleNavClick}
        data-tour={tourId}
        className={({ isActive }) =>
          mergeClasses(s.navItem, isActive ? s.navActive : undefined)
        }
      >
        <Icon />
        {t.nav[labelKey] as string}
        {to === 'usuarios' && canReadUsers && pendingRegistrationsCount > 0 && (
          <Badge data-testid="pending-registrations-badge" size="small" color="danger" appearance="filled">
            {pendingRegistrationsCount}
          </Badge>
        )}
        {to === 'registros-incompletos' && incompleteDraftsCount > 0 && (
          <Badge data-testid="incomplete-drafts-badge" size="small" color="danger" appearance="filled">
            {incompleteDraftsCount}
          </Badge>
        )}
      </NavLink>
    );
  };

  return (
    <aside className={mergeClasses(s.sidebar, isOpen ? s.sidebarOpen : undefined)}>
      {/* Close button — only visible on mobile */}
      <div className={s.closeBtn}>
        <button className={s.closeBtnInner} onClick={onClose} type="button" aria-label={t.nav.closeMenu}>
          <Dismiss24Regular />
        </button>
      </div>

      {/* Logo / Tenant header */}
      <div className={s.logoWrap}>
        <div className={s.logoContainer}>
          <img src="/agrovid-logo.svg" alt="Agrovid" className={s.logoImg} />
        </div>
        <div className={s.logoSub}>Empresa Group</div>
      </div>

      {TOP_NAV.some(canRender) && TOP_NAV.map(renderLink)}

      {DAY_NAV.some(canRender) && (
        <>
          <div className={s.sectionLabel}>{t.nav.myDay}</div>
          {DAY_NAV.map(renderLink)}
        </>
      )}

      {NAV_GROUPS.map((group) => {
        const visible = group.items.filter(canRender);
        if (visible.length === 0) return null; // grupo vacío para este usuario: no se muestra
        // Una sola opción (p. ej. el Vigilante solo ve Caseta): sin encabezado de sección, solo el enlace.
        if (visible.length === 1) {
          return (
            <div key={group.id}>
              <div className={s.divider} />
              {renderLink(visible[0])}
            </div>
          );
        }
        const isOpen = expandAll || !!openGroups[group.id];
        const label = t.nav.groups[group.id];
        const regionId = `nav-group-${group.id}`;
        return (
          <div key={group.id}>
            <div className={s.divider} />
            <button
              type="button"
              className={s.groupHeader}
              aria-expanded={isOpen}
              aria-controls={regionId}
              onClick={() => toggleGroup(group)}
            >
              {isOpen ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
              <span className={s.groupLabel}>{label}</span>
              {group.id === 'admin' && canReadUsers && pendingRegistrationsCount > 0 && !isOpen && (
                <Badge data-testid="nav-group-badge-admin" size="small" color="danger" appearance="filled">
                  {pendingRegistrationsCount}
                </Badge>
              )}
            </button>
            <div id={regionId} className={mergeClasses(s.groupBody, !isOpen && s.groupBodyClosed)} hidden={!isOpen}>
              {group.items.map(renderLink)}
            </div>
          </div>
        );
      })}
    </aside>
  );
}
